/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import PersonIcon from "@mui/icons-material/Person";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

import { useMaterialUIController } from "context";
import CategoryMenuController from "components/CategoryMenuController";
import { fetchPublicCategoryNames } from "utils/categoryApi";
import kljLogo from "assets/images/kljLogo.jpg";

function CommonStoreHeader({ children, profileInitials }) {
  const [controller] = useMaterialUIController();
  const { cartItems } = controller;
  const [categories, setCategories] = useState([]);
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  const cartItemCount = (Array.isArray(cartItems) ? cartItems : []).reduce(
    (total, item) => total + (Number(item?.quantity) || 0),
    0
  );

  const resolvedInitials = useMemo(() => {
    const fromProp = String(profileInitials || "").trim();
    if (fromProp) return fromProp.toUpperCase();

    const token = localStorage.getItem("token");
    if (!token) return "NA";

    try {
      const payloadPart = token.split(".")[1];
      if (!payloadPart) return "NA";
      const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
      const json = JSON.parse(atob(padded));

      const identity = String(
        json?.name ||
          json?.userName ||
          json?.email ||
          json?.phoneNumber ||
          json?.phoneNo ||
          json?.sub ||
          ""
      ).trim();

      if (!identity) return "NA";

      const parts = identity.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
      }

      return identity.slice(0, 2).toUpperCase();
    } catch (_error) {
      return "NA";
    }
  }, [profileInitials]);

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      try {
        const categoryNames = await fetchPublicCategoryNames();
        if (isMounted) {
          setCategories(categoryNames);
        }
      } catch (error) {
        console.error("Failed to load header categories:", error);
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box sx={{ position: "sticky", top: 0, zIndex: 1200 }}>
      <Box
        sx={{
          bgcolor: "#b71c2a",
          color: "#ffffff",
          py: 1.2,
          width: "100%",
        }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 3.5 } }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={{ xs: 1.5, lg: 2 }}
            alignItems={{ xs: "center", lg: "center" }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={1.8}
              alignItems="center"
              justifyContent={{ xs: "center", lg: "flex-start" }}
              sx={{ width: { xs: "100%", lg: "auto" } }}
              flex={1}
            >
              <Stack spacing={0.6} alignItems="center" sx={{ width: "fit-content" }}>
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    width: { xs: 56, md: 64 },
                    height: { xs: 56, md: 64 },
                    borderRadius: "50%",
                    border: "2px solid #ffffff",
                    bgcolor: "#8f1622",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    boxShadow: "inset 0 0 0 4px #8f1622, inset 0 0 0 7px #ffffff",
                    textDecoration: "none",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={kljLogo}
                    alt="KLJ Logo"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: "#facc15",
                    fontWeight: 600,
                    fontSize: { xs: "0.8rem", md: "0.88rem" },
                    lineHeight: 1.1,
                    letterSpacing: "0.01em",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {
                    "\u0DC4\u0DD2\u0DAD\u0DDA \u0DAD\u0DD2\u0DBA\u0DB1 \u0DC4\u0DD0\u0DB8\u0DAF\u0DDA\u0DB8"
                  }
                </Typography>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={0}
              alignItems="stretch"
              justifyContent={{ xs: "center", lg: "flex-end" }}
              sx={{
                border: "1px solid rgba(255,255,255,0.28)",
                borderRadius: 0.4,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  minWidth: { xs: 220, sm: 230 },
                  bgcolor: "#b71c2a",
                  px: 2.3,
                  py: 1.05,
                  borderRight: { xs: "none", sm: "1px solid rgba(255,255,255,0.35)" },
                  borderBottom: { xs: "1px solid rgba(255,255,255,0.35)", sm: "none" },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  {isLoggedIn && (
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        bgcolor: "#ffe4e6",
                        color: "#9f1239",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "0.8rem",
                      }}
                    >
                      {resolvedInitials}
                    </Box>
                  )}
                  <PersonIcon sx={{ fontSize: 20, color: "#ffffff" }} />
                  <Stack spacing={0.2} alignItems="flex-start">
                    <Typography
                      sx={{
                        color: "#ffffff",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        lineHeight: 1.1,
                      }}
                    >
                      {isLoggedIn ? "PROFILE" : "SIGN IN"}
                    </Typography>
                    {isLoggedIn ? (
                      <Stack direction="row" spacing={0.9} alignItems="center">
                        <Typography
                          component={Link}
                          to="/profile"
                          sx={{
                            color: "#ffffff",
                            textDecoration: "none",
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            lineHeight: 1.1,
                          }}
                        >
                          Profile
                        </Typography>
                        <Typography sx={{ color: "#ffffff", opacity: 0.85 }}>|</Typography>
                        <Typography
                          component={Link}
                          to="/my-orders"
                          sx={{
                            color: "#ffffff",
                            textDecoration: "none",
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            lineHeight: 1.1,
                          }}
                        >
                          Orders
                        </Typography>
                        <Typography sx={{ color: "#ffffff", opacity: 0.85 }}>|</Typography>
                        <Typography
                          component={Link}
                          to="/authentication/sign-out"
                          sx={{
                            color: "#ffffff",
                            textDecoration: "none",
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            lineHeight: 1.1,
                          }}
                        >
                          Logout
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography
                        component={Link}
                        to="/authentication/mobile-sign-in"
                        sx={{
                          color: "#ffffff",
                          textDecoration: "none",
                          fontWeight: 800,
                          fontSize: "1.75rem",
                          lineHeight: 1.1,
                        }}
                      >
                        ACCOUNT
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Box>

              <Box
                component={Link}
                to="/cart"
                sx={{
                  minWidth: { xs: 220, sm: 200 },
                  bgcolor: "#b71c2a",
                  px: 2.3,
                  py: 1.05,
                  textDecoration: "none",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <ShoppingCartOutlinedIcon sx={{ color: "#ffffff", fontSize: 24 }} />
                    <Box
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: -12,
                        minWidth: 20,
                        height: 20,
                        borderRadius: "50%",
                        bgcolor: "#000000",
                        color: "#ffffff",
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 0.4,
                      }}
                    >
                      {cartItemCount}
                    </Box>
                  </Box>
                  <Stack spacing={0.2} alignItems="flex-start">
                    <Typography
                      sx={{
                        color: "#ffffff",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        lineHeight: 1.1,
                      }}
                    >
                      CHECK OUT
                    </Typography>
                    <Typography
                      sx={{
                        color: "#ffffff",
                        fontWeight: 800,
                        fontSize: "1.75rem",
                        lineHeight: 1.1,
                      }}
                    >
                      CART
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
          px: { xs: 1.5, sm: 2.5, md: 3.5 },
          py: 1.1,
        }}
      >
        {children || <CategoryMenuController categories={categories} />}
      </Box>
    </Box>
  );
}

CommonStoreHeader.defaultProps = {
  children: null,
  profileInitials: "",
};

CommonStoreHeader.propTypes = {
  children: PropTypes.node,
  profileInitials: PropTypes.string,
};

export default CommonStoreHeader;
